import { observer } from "mobx-react";
import PolicyStore from "../../../stores/policy-store";
import Card from "../../../../../components/layouts/card/card";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import ExchangeProcess from "./components/exchange-process/exchange-process";
import MarkAsDone from "./components/mark-as-done/mark-as-done";
import RefundProcess from "./components/refund-process/refund-process";
import { APPROVAL_PROCESS } from "../../../constants";
import ReserveItems from "./components/reserve-items/reserve-items";
import { RESOLUTION_TYPE } from "../../../../resolutions/constants";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import './policy-rma-handling-card.scss';
import { ExpandCallapseStore } from "../../../../../hooks/expand-collapse-hook";
import OfferInsantExchange from "./components/offer-instant-exchange/offer-instant-exchange";
import NotifyHelpdesk from "./components/notify-helpdesk/notify-helpdesk";

interface IProps {
    policy: PolicyStore;
    validationGroup: IValidationGroup;
    expandCollapseStore: ExpandCallapseStore;
}

const RMAHandlingCard: React.FC<IProps> = observer(({policy, validationGroup, expandCollapseStore}) => {
    const selectedApprovalProcessOption = policy.approvalProcessOptions.find(o => o.value === policy.isAutoApproved);

    const getApprovalProcessDescription = () => {
        if (!policy.resolution || policy.selectedResolutionType === RESOLUTION_TYPE.CancelOrderType) {
            return "When manual, you will have to approve each return request (RMA).";
        }
        return 'Automate approval for return instructions to be provided to customers in the portal and emailed straight away.';
    }

    const disabled = policy.selectedResolutionType === RESOLUTION_TYPE.InstantRefundType;

    return (
        <Card toolbar={{ title: (
            <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>RMA Handling Process</FontBody1>
            ) }} 
            isExpanded={expandCollapseStore.getIsExpanded('rma-handling-card')}
            expandCallback={(v) => expandCollapseStore.setExpanded('rma-handling-card', v)}
        >
            <FontBody1 color={FONT_COLOR.PRIMARY3_50}>Set how each step will be processed when customers choose this policy rule.</FontBody1>

            <FormFieldLine
                label='Approval Process'
                note={getApprovalProcessDescription()}
                tooltip={<div className="rma-process-tooltip">
                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        The RMA will be automatically approved only if
                        all policy rules selected in the RMA are set on auto-approval.
                    </FontBody2>
                </div>}
            >
                <SelectDropdown
                    value={selectedApprovalProcessOption}
                    options={policy.approvalProcessOptions}
                    disabled={disabled}
                    onChange={(v) => policy.setIsAutoApproved(v?.value as APPROVAL_PROCESS)}
                    validationGroup={validationGroup} 
                    required={true}
                    isSearchable={false}
                 />
            </FormFieldLine>

            <ExchangeProcess policy={policy} validationGroup={validationGroup} /> 

            <RefundProcess policy={policy} validationGroup={validationGroup} />

            <MarkAsDone policy={policy} validationGroup={validationGroup} />

            {policy.shouldShowInstantExchangeSettings && <OfferInsantExchange policy={policy} validationGroup={validationGroup} />}

            {policy.isReserveItemsSupported && <ReserveItems policy={policy} />}

            <NotifyHelpdesk policy={policy} />
        </Card>
    );
});

export default RMAHandlingCard;